<template>
  <aside class="sidebar">
    <div class="brand">
      <img class="brand-mark" src="/favicon.svg" alt="LensPoint" width="24" height="24" />
      <div class="col" style="gap: 0">
        <span class="brand-name">LensPoint</span>
        <span class="muted brand-sub">Finance</span>
      </div>
    </div>

    <nav
      v-for="sec in navSections"
      :key="sec.label"
      class="nav-section"
      :aria-label="sec.label"
    >
      <div class="nav-label">{{ sec.label }}</div>
      <RouterLink
        v-for="it in sec.items"
        :key="it.id"
        :to="it.to"
        v-slot="{ isActive, navigate }"
        custom
      >
        <div
          class="nav-item"
          :aria-current="isActive ? 'page' : undefined"
          tabindex="0"
          role="link"
          @click="navigate"
          @keydown.enter="() => navigate()"
          @keydown.space.prevent="() => navigate()"
        >
          <Icon :name="it.icon" :size="16" class="nav-icon" />
          <span>{{ it.label }}</span>
          <span v-if="it.badge" class="badge">{{ it.badge }}</span>
          <span v-else-if="it.kbd" class="kbd">{{ it.kbd }}</span>
        </div>
      </RouterLink>
    </nav>

    <div class="nav-section">
      <div class="nav-label">Scope</div>
      <ScopeSwitcher />
    </div>

    <!-- Bottom group. It owns the single `margin-top: auto` for the panel —
         giving both children one would split the leftover space between them
         and leave the feedback row stranded mid-panel. -->
    <div class="sidebar-foot">
      <!-- Opens a dialog rather than navigating, so it can't be a RouterLink,
           but it reads as a nav row so it doesn't look bolted on. -->
      <button class="nav-item feedback-item" type="button" @click="openFeedback">
        <Icon name="megaphone" :size="16" class="nav-icon" />
        <span>Send feedback</span>
      </button>
      <AccountPill />
    </div>
  </aside>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router'
import Icon from './Icon.vue'
import ScopeSwitcher from './ScopeSwitcher.vue'
import AccountPill from './AccountPill.vue'
import { openFeedback } from '@/composables/useFeedback'

interface NavItem {
  id: string
  label: string
  icon: string
  to: string
  kbd?: string
  badge?: string
}

interface NavSection {
  label: string
  items: NavItem[]
}

const navSections: NavSection[] = [
  {
    label: 'Money',
    items: [
      { id: 'dashboard', label: 'Overview', icon: 'dashboard', to: '/overview' },
      { id: 'expenses',  label: 'Expenses', icon: 'ledger',    to: '/expenses' },
      { id: 'rules',     label: 'Rules',    icon: 'rules',     to: '/rules' },
      { id: 'accounts',  label: 'Accounts', icon: 'bank',      to: '/accounts' },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { id: 'chat',     label: 'Expense AI', icon: 'chat', to: '/chat' },
      { id: 'profile',  label: 'Settings',   icon: 'gear', to: '/settings' },
    ],
  },
]
</script>

<style scoped>
/* The nav is its own panel: a shade darker than the page, which separates it
   without a divider rule. */
.sidebar {
  border-right: 0;
  background: var(--nav-bg);
  display: flex;
  flex-direction: column;
  /* Clears the status bar when open as a drawer under viewport-fit=cover. */
  padding: calc(16px + env(safe-area-inset-top, 0px)) 12px
           calc(16px + env(safe-area-inset-bottom, 0px));
  gap: 18px;
  position: sticky;
  top: 0;
  height: 100vh;
}

.brand {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 4px 8px 14px;
  margin-bottom: 0;
}
.brand-mark {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: block;
  flex-shrink: 0;
}
.brand-name {
  font-weight: 600;
  letter-spacing: -0.015em;
  font-size: 13.5px;
}
/* Matches the wordmark's face — the mono cut read as a different brand.
   Sentence case per the casing spec; only the nav section labels are uppercase. */
.brand-sub {
  font-size: 10.5px;
  letter-spacing: 0.01em;
  font-family: var(--font-sans);
  font-weight: 500;
}

.nav-section {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
/* Section labels sit on the darker nav panel, so the old --ink-4 nearly
   dissolved into it. --ink-3 gives them presence without adding weight. */
.nav-label {
  font-size: 10px;
  text-transform: uppercase;
  color: var(--ink-3);
  letter-spacing: 0.08em;
  padding: 8px 10px 4px;
  font-weight: 500;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 9px;
  border-radius: 7px;
  color: var(--ink-2);
  font-size: 12.5px;
  font-weight: 400;
  cursor: pointer;
  user-select: none;
  border: 0;
  outline: none;
  transition: background var(--transition-fast, 0.15s), color var(--transition-fast, 0.15s);
}
/* Icon tracks the row's text colour (muted → ink on hover/active) and eases
   the change so the bigger glyphs feel responsive rather than snapping. */
.nav-item .nav-icon { transition: color var(--transition-fast, 0.15s); }
/* Ink-tinted wash rather than a surface colour — the nav panel is darker than
   the page, so surface-2 would read as a *lighter* pill on hover. */
.nav-item:hover {
  background: color-mix(in oklab, var(--ink) 7%, transparent);
  color: var(--ink);
}
.nav-item:focus-visible {
  background: color-mix(in oklab, var(--ink) 7%, transparent);
  color: var(--ink);
  outline: var(--focus-ring);
  outline-offset: -2px;
}
/* Active page: lifted off the nav panel and set in semibold. */
.nav-item[aria-current="page"] {
  background: var(--surface);
  color: var(--ink);
  font-weight: 600;
  box-shadow: var(--shadow-sm);
}
/* Absorbs the leftover height so the group stays pinned to the bottom whether
   or not the nav sections fill the sidebar. */
.sidebar-foot {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.feedback-item {
  width: 100%;
  font: inherit;
  font-size: 12.5px;
  color: var(--ink-3);
  background: transparent;
  text-align: left;
}
.feedback-item:hover { color: var(--ink); }

.nav-item .badge {
  margin-left: auto;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--ink-3);
}
.nav-item .kbd {
  margin-left: auto;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--ink-4);
  background: color-mix(in oklab, var(--ink) 7%, transparent);
  border: 0;
  padding: 1px 5px;
  border-radius: 4px;
}
.nav-item[aria-current="page"] .kbd { background: var(--surface-2); }

/* Drawer mode (matches AppShell's breakpoint): the sidebar is a touch
   surface, so nav rows grow to comfortable tap heights. */
@media (max-width: 880px) {
  .sidebar {
    padding: calc(18px + env(safe-area-inset-top, 0px)) 14px
             calc(18px + env(safe-area-inset-bottom, 0px));
    gap: 20px;
    overflow-y: auto;
  }
  .nav-item {
    padding: 13px 12px;
    gap: 12px;
    font-size: 14.5px;
    border-radius: 9px;
  }
  .nav-item .icon { width: 17px; height: 17px; }
  .nav-label {
    font-size: 11px;
    padding: 10px 12px 6px;
  }
  .brand { padding-bottom: 12px; }
}
</style>
